import FormField from '@/components/ui/FormField'
import { isReadOnly } from '@/lib/permissions'
import type { APIUserDetail } from '@/services/users'
import { colors, sxPresets } from '@/theme'
import { zodResolver } from '@hookform/resolvers/zod'
import { Edit as EditIcon, Person as PersonIcon } from '@mui/icons-material'
import { Box, Button, IconButton, Paper, Typography } from '@mui/material'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

const userDetailsSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().min(1, 'Email address is required').email('Please enter a valid email address'),
  mobile: z.string().optional(),
  ssoProvider: z.string().optional()
})

type UserDetailsFormData = z.infer<typeof userDetailsSchema>

interface UserDetailsProps {
  user: APIUserDetail
  onSave?: (updatedUser: Partial<APIUserDetail>) => void
}

export default function UserDetails({ user, onSave }: UserDetailsProps) {
  const readOnly = isReadOnly()
  const [isEditMode, setIsEditMode] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<UserDetailsFormData>({
    resolver: zodResolver(userDetailsSchema),
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      mobile: user.mobile,
      ssoProvider: user.ssoProvider || ''
    }
  })

  const handleEdit = () => {
    setIsEditMode(true)
  }

  const handleCancel = () => {
    setIsEditMode(false)
    reset({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      mobile: user.mobile,
      ssoProvider: user.ssoProvider || ''
    })
  }

  const onSubmit = (data: UserDetailsFormData) => {
    if (readOnly) return
    onSave?.(data)
    setIsEditMode(false)
  }

  return (
    <Paper
      id='user-details'
      elevation={1}
      component='form'
      onSubmit={handleSubmit(onSubmit)}
      sx={{ border: `1px solid ${colors.border.default}`, overflow: 'hidden' }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: 3, pt: 3 }}>
        <Typography variant='sectionTitle'>User details</Typography>
        {!isEditMode && !readOnly && (
          <IconButton onClick={handleEdit} size='small' color='primary' type='button'>
            <EditIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        )}
        {isEditMode && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant='primary' type='submit' sx={{ width: '200px' }}>
              Save information
            </Button>
            <Button variant='ghost' onClick={handleCancel} type='button'>
              Cancel
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, px: 3, pb: 3 }}>
        {/* Row 1 */}
        <Box
          sx={{
            ...sxPresets().grid.fourColumn,
            '& > *:not(:last-child)': { borderRight: `1px solid ${colors.border.default}`, pr: 3 }
          }}
        >
          <FormField
            name='firstName'
            label='First name'
            control={control}
            isEditMode={isEditMode}
            error={errors.firstName?.message}
          >
            <PersonIcon sx={{ fontSize: '16px' }} />
          </FormField>
          <FormField
            name='lastName'
            label='Last name'
            control={control}
            isEditMode={isEditMode}
            error={errors.lastName?.message}
          >
            <PersonIcon sx={{ fontSize: '16px' }} />
          </FormField>
          <FormField
            name='email'
            label='Email address'
            control={control}
            isEditMode={isEditMode}
            error={errors.email?.message}
          >
            <PersonIcon sx={{ fontSize: '16px' }} />
          </FormField>
          <FormField
            name='mobile'
            label='Mobile'
            control={control}
            isEditMode={isEditMode}
            error={errors.mobile?.message}
          />
        </Box>

        {/* Row 2 */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gap: 3 }}>
          <FormField
            name='ssoProvider'
            label='SSO provider'
            control={control}
            isEditMode={isEditMode}
            error={errors.ssoProvider?.message}
          />
          <Box /> {/* Empty space for layout */}
        </Box>
      </Box>
    </Paper>
  )
}
